// shared/workspace-permissions.ts
//
// Single source of truth for the workspace role -> capability matrix.
// Imported by BOTH the server policy layer (src/server/workspace/authorize.ts)
// and the client hook (useWorkspacePermissions). This file must stay free of
// server-only imports so it is safe to bundle into client components.
//
// Role hierarchy: admin > member > guest. The workspace OWNER is a member row
// with role "admin" plus Workspace.ownerId — a handful of capabilities are
// owner-only (billing, delete, transfer) and are NOT grantable to non-owner
// admins; those are gated by `isOwner`, not by role.

export type WorkspaceRole = "admin" | "member" | "guest";

export type Capability =
  | "content:read" // view dashboards / reports / synced data / leads
  | "content:write" // create/edit dashboards, reports, forms
  | "data:sync" // trigger data refresh / sync
  | "data:export" // export data
  | "integration:status" // see connected state + account name
  | "integration:read" // see masked keys / integration management screen
  | "integration:manage" // add / remove / reconnect / relabel integrations
  | "member:read" // list members
  | "member:invite" // send / revoke invitations
  | "member:manage" // change roles / remove members
  | "workspace:rename" // rename + branding
  | "workspace:audit" // view audit log
  | "workspace:delete" // owner-only
  | "workspace:transfer" // owner-only
  | "workspace:billing"; // owner-only (Stripe portal on the owner's personal sub)

const GUEST_CAPABILITIES: Capability[] = [
  "content:read",
  "data:export",
  "integration:status",
];

const MEMBER_CAPABILITIES: Capability[] = [
  ...GUEST_CAPABILITIES,
  "content:write",
  "data:sync",
  "member:read",
];

const ADMIN_CAPABILITIES: Capability[] = [
  ...MEMBER_CAPABILITIES,
  "integration:read",
  "integration:manage",
  "member:invite",
  "member:manage",
  "workspace:rename",
  "workspace:audit",
];

export const ROLE_CAPABILITIES: Record<WorkspaceRole, Capability[]> = {
  guest: GUEST_CAPABILITIES,
  member: MEMBER_CAPABILITIES,
  admin: ADMIN_CAPABILITIES,
};

// Capabilities that ONLY the workspace owner has, regardless of role.
export const OWNER_ONLY_CAPABILITIES: Capability[] = [
  "workspace:delete",
  "workspace:transfer",
  "workspace:billing",
];

export const ROLE_RANK: Record<WorkspaceRole, number> = {
  guest: 1,
  member: 2,
  admin: 3,
};

export function roleAtLeast(role: WorkspaceRole, min: WorkspaceRole): boolean {
  return ROLE_RANK[role] >= ROLE_RANK[min];
}

export function roleHasCapability(
  role: WorkspaceRole,
  capability: Capability,
): boolean {
  return ROLE_CAPABILITIES[role].includes(capability);
}

export interface CapabilitySubject {
  role: WorkspaceRole;
  isOwner: boolean;
}

/**
 * The authoritative capability check. Owner-only capabilities require
 * `isOwner`; everything else is decided by the role matrix.
 */
export function hasCapability(
  capability: Capability,
  subject: CapabilitySubject,
): boolean {
  if (OWNER_ONLY_CAPABILITIES.includes(capability)) {
    return subject.isOwner;
  }
  return roleHasCapability(subject.role, capability);
}

/**
 * Full set of capabilities for a subject — convenient for the client hook to
 * precompute once per active workspace.
 */
export function capabilitiesFor(subject: CapabilitySubject): Capability[] {
  const base = new Set<Capability>(ROLE_CAPABILITIES[subject.role]);
  if (subject.isOwner) {
    for (const cap of OWNER_ONLY_CAPABILITIES) base.add(cap);
  }
  return Array.from(base);
}

/**
 * The core data-isolation decision, shared by EVERY workspace-scoped resource
 * (integration keys, dashboards, reports, forms, leads): does a resource tagged
 * with `resourceWorkspaceId` belong to the active workspace?
 *
 *   - A tagged resource belongs to exactly the workspace it is tagged with.
 *   - A legacy UNTAGGED resource (a record created before the backfill) belongs
 *     only to the OWNER'S PERSONAL workspace, so it can never be reached from a
 *     shared / non-personal workspace. This is the migration path for records
 *     with no workspace association: untagged ⇒ personal.
 *
 * Pure so it is unit-testable and shared by the policy layer, the key routes,
 * and the item-array (dashboards/reports/forms) read filters.
 */
export function isResourceInWorkspace(
  resourceWorkspaceId: string | null | undefined,
  activeWorkspaceId: string,
  activeIsPersonal: boolean,
): boolean {
  if (resourceWorkspaceId) return resourceWorkspaceId === activeWorkspaceId;
  return activeIsPersonal;
}

/** Isolation decision for a Mailchimp key. @see isResourceInWorkspace */
export function isKeyInWorkspace(
  keyWorkspaceId: string | null | undefined,
  activeWorkspaceId: string,
  activeIsPersonal: boolean,
): boolean {
  return isResourceInWorkspace(
    keyWorkspaceId,
    activeWorkspaceId,
    activeIsPersonal,
  );
}

/**
 * Isolation decision for an item-array resource (a dashboard, custom report, or
 * custom form stored on the owner's User doc, or a FormSubmission lead).
 * @see isResourceInWorkspace
 */
export function isItemInWorkspace(
  itemWorkspaceId: string | null | undefined,
  activeWorkspaceId: string,
  activeIsPersonal: boolean,
): boolean {
  return isResourceInWorkspace(
    itemWorkspaceId,
    activeWorkspaceId,
    activeIsPersonal,
  );
}

/**
 * Filter an array of workspace-taggable items (each may carry a `workspaceId`)
 * to those visible in the active workspace, applying the untagged ⇒ personal
 * rule via isResourceInWorkspace. Used by the item-array read routes
 * (dashboards / custom reports / custom forms live on the owner's User doc).
 */
export function filterByWorkspace<T extends { workspaceId?: unknown }>(
  items: readonly T[] | null | undefined,
  activeWorkspaceId: string,
  activeIsPersonal: boolean,
): T[] {
  if (!Array.isArray(items)) return [];
  return items.filter((it) =>
    isResourceInWorkspace(
      it && it.workspaceId != null ? String(it.workspaceId) : null,
      activeWorkspaceId,
      activeIsPersonal,
    ),
  );
}

/**
 * Resolve the active Mailchimp key for a workspace, migration-safe (pure).
 *
 * Preference order:
 *   1. the workspace's own mirrored selection (`Workspace.selectedMailchimpKeyId`);
 *   2. the owner profile's selected key — ONLY if it belongs to this workspace
 *      (keeps pre-workspace accounts, whose selection lived on the profile,
 *      working WITHOUT ever borrowing another workspace's key);
 *   3. the connected key in the workspace, else the first key;
 *   4. null when the workspace has no integration (→ empty/zero state).
 *
 * The returned key always belongs to the active workspace, so it passes
 * `assertKeyAccess`. The server wrapper lives in
 * `src/server/workspace/mailchimp-scope.ts`.
 */
export function resolveActiveKeyIdPure(opts: {
  keys:
    | ReadonlyArray<{
        keyId?: string;
        connected?: boolean;
        workspaceId?: unknown;
      }>
    | null
    | undefined;
  activeWorkspaceId: string;
  activeIsPersonal: boolean;
  workspaceSelectedKeyId?: string | null;
  profileSelectedKeyId?: string | null;
}): string | null {
  const keys = Array.isArray(opts.keys) ? opts.keys : [];
  const inWs = keys.filter((k) =>
    isKeyInWorkspace(
      k?.workspaceId != null ? String(k.workspaceId) : null,
      opts.activeWorkspaceId,
      opts.activeIsPersonal,
    ),
  );
  const ids = new Set(
    inWs.map((k) => k.keyId).filter((id): id is string => !!id),
  );

  const wsSelected = opts.workspaceSelectedKeyId || null;
  if (wsSelected && ids.has(wsSelected)) return wsSelected;

  const profileSelected = opts.profileSelectedKeyId || null;
  if (profileSelected && ids.has(profileSelected)) return profileSelected;

  const connected = inWs.find((k) => k.connected && k.keyId);
  if (connected?.keyId) return connected.keyId;

  return inWs[0]?.keyId || null;
}

/**
 * A MongoDB filter fragment that restricts a COLLECTION query to the active
 * workspace (e.g. FormSubmission leads). On a personal workspace, untagged /
 * legacy docs are included (untagged ⇒ personal); on a shared workspace only
 * exactly-tagged docs match. Merge into an existing query object. Returned as a
 * plain object so it is pure/testable; mongoose casts the id string to ObjectId.
 */
export function workspaceMongoFilter(
  activeWorkspaceId: string,
  activeIsPersonal: boolean,
  field = "workspaceId",
): Record<string, unknown> {
  if (activeIsPersonal) {
    return {
      $or: [
        { [field]: activeWorkspaceId },
        { [field]: null },
        { [field]: { $exists: false } },
      ],
    };
  }
  return { [field]: activeWorkspaceId };
}

/**
 * Whether a plan type grants Premium features. The SINGLE source of truth for
 * the "is this plan premium?" decision, shared by the owner-derived entitlements
 * resolver (`getWorkspaceEntitlements`), the `/api/workspaces` rows, and the
 * client permission hook — so a workspace's premium status (owner-derived, §WS1)
 * is computed the same way everywhere and a Basic member of a Premium workspace
 * resolves premium. Matches every PREMIUM variant (e.g. PREMIUM_MONTHLY,
 * TRIAL_PREMIUM); pure, so it is unit-testable.
 */
export function isPremiumPlan(planType: string | null | undefined): boolean {
  return (planType || "").toUpperCase().includes("PREMIUM");
}

/**
 * Whether a plan type is BASIC (or absent). The counterpart to isPremiumPlan and
 * the single source of truth for the "no premium features" decision — shared by
 * the header Edit-button gate and the /dashboard/edit + /dashboard/report-builder
 * page guards so a button and the page it links to always agree. A missing plan
 * counts as basic. Note STANDARD is neither basic nor premium.
 */
export function isBasicPlan(planType: string | null | undefined): boolean {
  const plan = (planType || "").toUpperCase();
  return !plan || plan.includes("BASIC");
}

/**
 * Total workspaces an owner may own by plan (decided §E-2, "Standard gets a
 * few"): BASIC = personal only (1), STANDARD = 3, PREMIUM = unlimited. Pure so
 * both the entitlements resolver and tests share it.
 */
export function getMaxOwnedWorkspaces(
  planType: string | null | undefined,
): number {
  const plan = (planType || "").toUpperCase();
  if (isPremiumPlan(plan)) return Infinity;
  if (plan.includes("STANDARD")) return 3;
  return 1; // BASIC and anything else: personal workspace only
}
